import * as Flashphoner from '@flashphoner/websdk';
import { STREAM_EVENT, STREAM_EVENT_TYPE } from '@flashphoner/websdk/src/constants';
import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled, { css } from 'styled-components';

import { addCapture } from 'services/api-service/center';
import { ControlCommand, ControlValue, isControlEnabled, StreamState } from 'services/control-protocol';
import { utilService } from 'services/util-service';
import { IStoreState } from 'store/modules';
import { ActionCtor } from 'store/modules/main';
import { captureService } from '../../../services/capture-service';
import * as FPUtils from '../../modules/fp-utils';

const STREAM_STATUS = Flashphoner.constants.STREAM_STATUS;
const Browser = Flashphoner.Browser;
const PRELOADER_URL = process.env.PUBLIC_URL + '/media/preloader.mp4';
const CAMERA_PENDING_TIMEOUT_MS = 5000;
const COLOR = {
  r: css`
    background: linear-gradient(180deg, #0086ff 0%, #005099 100%);
  `,
  v: css`
    background: linear-gradient(180deg, #e63528 0%, #801d16 100%);
  `,
};

interface StreamLiveProps {
  index: number;
  expand: number;
  participants?: any;
  onExpand: (index: number) => void;
  onUpateMedia: () => void;
  onControl: (cmd: ControlCommand, value: ControlValue, name: string) => void;
}

const StreamLive = ({ index, expand, participants, onExpand, onUpateMedia, onControl }: StreamLiveProps) => {
  const dispatch = useDispatch();
  const users = useSelector((store: IStoreState) => store.MainReducer.users);
  const selectedRoom = useSelector((store: IStoreState) => store.MainReducer.selectedRoom);
  const rescues = useSelector((store: IStoreState) => store.MainReducer.rescues);
  const deviceReports = useSelector((store: IStoreState) => store.MainReducer.deviceReports);
  const [participant, setParticipant] = useState<any>(undefined);
  const [type, setType] = useState('r');
  const [size, setSize] = useState('320x240');
  const [bitrate, setBitrate] = useState(0);
  const [captureUri, setCaptureUri] = useState<string | null>(null);
  const [video, setVideo] = useState<any | null>(null);
  const [hasVideoFrame, setHasVideoFrame] = useState(false);
  const [isAudioMuted, setIsAudioMuted] = useState(true);
  const [isVideoMuted, setIsVideoMuted] = useState(true);
  const [pendingFacing, setPendingFacing] = useState<'front' | 'rear' | null>(null);
  const videoBytesReceived = useRef<number | null>(null);
  const statsTimer = useRef<ReturnType<typeof setInterval> | null>(null);

  const clearStatsTimer = () => {
    if (statsTimer.current !== null) {
      clearInterval(statsTimer.current);
      statsTimer.current = null;
    }
  };

  const playStream = (stream, remoteVideo) => {
    stream
      .play(remoteVideo)
      .on(STREAM_STATUS.PLAYING, (playingStream) => {
        setVideo(playingStream);
        if (playingStream.getVideoState) {
          setIsVideoMuted(playingStream.getVideoState().muted);
        }
        if (playingStream.getAudioState) {
          setIsAudioMuted(playingStream.getAudioState().muted);
        }
        const video = document.getElementById(playingStream.id()) as HTMLVideoElement | null;
        if (video) {
          video.addEventListener('resize', (event: any) => {
            if (event.target !== null) {
              setSize(event.target.videoWidth + 'x' + event.target.videoHeight);
            }
            FPUtils.resizeVideo(event.target);
          });
          const syncFrameState = () => {
            setHasVideoFrame(video.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA && video.videoWidth > 0);
          };
          video.addEventListener('loadeddata', syncFrameState);
          video.addEventListener('playing', syncFrameState);
          video.addEventListener('resize', syncFrameState);
          video.addEventListener('waiting', syncFrameState);
          video.addEventListener('emptied', () => setHasVideoFrame(false));
          syncFrameState();
          clearStatsTimer();
          videoBytesReceived.current = null;
          statsTimer.current = setInterval(() => {
            loadStats(playingStream);
          }, 5000);
        }
      })
      .on(STREAM_STATUS.STOPPED, () => {
        clearStatsTimer();
        setVideo(null);
        setHasVideoFrame(false);
      })
      .on(STREAM_EVENT, (streamEvent) => {
        switch (streamEvent.type) {
          case STREAM_EVENT_TYPE.VIDEO_MUTED:
            setIsVideoMuted(true);
            break;
          case STREAM_EVENT_TYPE.VIDEO_UNMUTED:
            setIsVideoMuted(false);
            break;
          case STREAM_EVENT_TYPE.AUDIO_MUTED:
            setIsAudioMuted(true);
            break;
          case STREAM_EVENT_TYPE.AUDIO_UNMUTED:
            setIsAudioMuted(false);
            break;
        }
      })
      .on(STREAM_STATUS.FAILED, () => {
        //Remove Stream
        stream.stop();
        clearStatsTimer();
        setVideo(null);
        setHasVideoFrame(false);
        dispatch(ActionCtor.removePublisher(participant));
        console.log('FAILED: ' + participant.name());
      });
  };

  const playParticipantStream = (roomParticipant) => {
    let remoteVideo: HTMLElement | null = null;
    let stream = null;
    const name = roomParticipant.name();

    if (roomParticipant.getStreams().length > 0) {
      stream = roomParticipant.getStreams()[0];
      remoteVideo = document.getElementById('remoteVideo-' + name);
      if (stream && remoteVideo) {
        // Remove Cached Stream
        for (let i = 0; i < remoteVideo.children.length; i++) {
          if (remoteVideo.children[i]) {
            remoteVideo.removeChild(remoteVideo.children[i]);
          }
        }
        if (Browser.isSafariWebRTC()) {
          Flashphoner.playFirstVideo(remoteVideo, false, PRELOADER_URL).then(() => {
            playStream(stream, remoteVideo);
          });
          return;
        }
        playStream(stream, remoteVideo);
      }
    }
  };

  const onCapture = async () => {
    if (video === null) {
      return;
    }
    const frame: any = captureService.captureVideoFrame(video.id(), 'png');
    if (!frame) {
      console.log('captureVideoFrame Failed');
      return;
    }
    // Show Image
    setCaptureUri(frame.dataUri);
    // Upload Image
    if (selectedRoom !== undefined) {
      const formData = new FormData();
      formData.append('roomId', selectedRoom);
      formData.append('file', frame.blob);
      // Fetch
      try {
        const response: any = await addCapture(formData);
        if (response.result === 'success') {
          onUpateMedia();
        }
      } catch (error) {
        console.log('addCapture Error ', error);
      }
    }
  };

  const loadStats = (stream: any) => {
    if (stream !== null) {
      stream.getStats(function (stats) {
        if (stats && stats.inboundStream) {
          if (stats.inboundStream.video) {
            const received = stats.inboundStream.video.bytesReceived;
            const previous = videoBytesReceived.current;
            videoBytesReceived.current = received;
            if (previous === null) {
              setBitrate(0);
              return;
            }
            const vBitrate = (received - previous) * 8;
            setBitrate(Math.floor(vBitrate / 1024 / 5));
          }
        }
      });
    }
  };

  function findByPhoneNumber(rescuePhone) {
    function traverse(node) {
      if (Array.isArray(node)) {
        for (const sub of node) {
          const found = traverse(sub);
          if (found) return found;
        }
        return null;
      }
      if (node.lv === 4 && Array.isArray(node.children)) {
        for (const child of node.children) {
          if (child.value === rescuePhone) {
            return child;
          }
        }
      }
      if (node.children && node.children.length > 0) {
        for (const sub of node.children) {
          const found = traverse(sub);
          if (found) return found;
        }
      }
      return null;
    }
    const result = traverse(rescues);
    return result ? result.vesselinfo : rescuePhone;
  }

  const report = participant !== undefined ? deviceReports[participant.name()] : undefined;
  const isAudioOn = report ? report.state.audio : !isAudioMuted;
  const isVideoOn = report ? report.state.video : !isVideoMuted;
  const compact = expand === 4;
  const icon = (name: string) => process.env.PUBLIC_URL + '/images/' + name;

  interface ControlSpec {
    key: string;
    enabled: boolean;
    src: string;
    disabledSrc?: string;
    alt: string;
    onClick: () => void;
  }

  const buildControls = (): ControlSpec[] => {
    if (participant === undefined) {
      return [];
    }
    const name = participant.name();
    const caps = report?.caps;
    const state = report?.state;
    const streamState: StreamState = {
      connected: video !== null,
      hasFrame: hasVideoFrame,
      videoOn: isVideoOn,
    };
    const specs: ControlSpec[] = [];

    if (type === 'r') {
      specs.push({
        key: 'video',
        enabled: isControlEnabled('video', caps, state, streamState),
        src: icon(isVideoOn ? 'btn_camera_on.svg' : 'btn_camera_off.svg'),
        alt: isVideoOn ? 'Camera On' : 'Camera Off',
        onClick: () => onControl('video', !isVideoOn, name),
      });
      specs.push({
        key: 'audio',
        enabled: isControlEnabled('audio', caps, state, streamState),
        src: icon(isAudioOn ? 'btn_mic_on.svg' : 'btn_mic_off.svg'),
        alt: isAudioOn ? 'Mic On' : 'Mic Off',
        onClick: () => onControl('audio', !isAudioOn, name),
      });
    }

    specs.push({
      key: 'capture',
      enabled: isControlEnabled('capture', caps, state, streamState),
      src: icon('btn_capture.svg'),
      alt: 'Capture',
      onClick: onCapture,
    });

    if (type === 'r') {
      const torchOn = state?.torch === true;
      specs.push({
        key: 'torch',
        enabled: isControlEnabled('torch', caps, state, streamState),
        src: icon(torchOn ? 'btn_flash_on.svg' : 'btn_flash_off.svg'),
        disabledSrc: icon('btn_flash_disabled.svg'),
        alt: torchOn ? 'Flash On' : 'Flash Off',
        onClick: () => onControl('torch', !torchOn, name),
      });
      const facing = state?.facing === 'front' ? 'front' : 'rear';
      specs.push({
        key: 'camera',
        enabled: pendingFacing === null && isControlEnabled('camera', caps, state, streamState),
        src: icon('btn_switch_camera.svg'),
        disabledSrc: icon('btn_switch_camera_disabled.svg'),
        alt: facing === 'front' ? 'To Rear Camera' : 'To Front Camera',
        onClick: () => {
          const target = facing === 'front' ? 'rear' : 'front';
          setPendingFacing(target);
          onControl('camera', target, name);
        },
      });
      specs.push({
        key: 'zoomOut',
        enabled: isControlEnabled('zoomOut', caps, state, streamState),
        src: icon('btn_zoom_out.svg'),
        disabledSrc: icon('btn_zoom_out_disabled.svg'),
        alt: 'Zoom Out',
        onClick: () => onControl('zoom', 'out', name),
      });
      specs.push({
        key: 'zoomIn',
        enabled: isControlEnabled('zoomIn', caps, state, streamState),
        src: icon('btn_zoom_in.svg'),
        disabledSrc: icon('btn_zoom_in_disabled.svg'),
        alt: 'Zoom In',
        onClick: () => onControl('zoom', 'in', name),
      });
    }
    return specs;
  };

  useEffect(() => {
    return () => {
      clearStatsTimer();
    };
  }, []);

  useEffect(() => {
    if (participant !== undefined) {
      onControl('caps', null, participant.name());
    }
    setPendingFacing(null);
  }, [participant]);

  useEffect(() => {
    if (pendingFacing === null || report === undefined) {
      return;
    }
    if (report.state.facing === pendingFacing || report.state.cameraError !== null || !report.state.video) {
      setPendingFacing(null);
    }
  }, [report]);

  useEffect(() => {
    if (pendingFacing === null) {
      return;
    }
    const timer = setTimeout(() => setPendingFacing(null), CAMERA_PENDING_TIMEOUT_MS);
    return () => clearTimeout(timer);
  }, [pendingFacing]);

  useEffect(() => {
    // Play Stream
    setCaptureUri(null);
    if (participant !== undefined) {
      playParticipantStream(participant);
    }
  }, [participant]);

  useEffect(() => {
    // Set participant
    if (participants !== undefined && participants.length > index) {
      if (participant !== undefined && participant.name() !== participants[index].name()) {
        if (participant.getStreams().length > 0) {
          const stream = participant.getStreams()[0];
          stream.stop();
        }
      }
      setParticipant(participants[index]);
    } else {
      if (participant !== undefined && participant.getStreams().length > 0) {
        const stream = participant.getStreams()[0];
        stream.stop();
      }
      setParticipant(undefined);
    }
  }, [index, participants]);

  useEffect(() => {
    // Find Type & Set
    if (participant !== undefined) {
      if (users.rDatas.find((data) => data.phone === participant.name()) !== undefined) {
        setType('r');
      } else if (users.vDatas.find((data) => data.phone === participant.name()) !== undefined) {
        setType('v');
      }
    }
  }, [users, participant]);

  return (
    <StreamLiveTemplate tabIndex={expand}>
      {participant !== undefined ? (
        <StreamWrap>
          <HeaderWrap color={type}>
            <Image src={process.env.PUBLIC_URL + '/images/img_subtract.svg'} alt="Subtract" />
            <TextWrap>
              {participant !== undefined &&
                (() => {
                  const phone = participant.name();
                  const nameInfo = findByPhoneNumber(phone);
                  return nameInfo === phone
                    ? utilService.isMobileFormat(phone)
                    : `${nameInfo} (${utilService.isMobileFormat(phone)})`;
                })()}
            </TextWrap>

            <RightImage
              onClick={() => onExpand(index)}
              src={
                expand === 4
                  ? process.env.PUBLIC_URL + '/images/btn_fullscreen.svg'
                  : process.env.PUBLIC_URL + '/images/btn_reduction.svg'
              }
              alt="Full"
            />
          </HeaderWrap>
          <VideoWrap>
            <div id={'remoteVideo-' + participant.name()}></div>
            <BottomWrap $compact={compact}>
              <BottomInfo $compact={compact}>
                <InfoText>
                  {size} / {bitrate} kbps
                </InfoText>
              </BottomInfo>
              <ControlGroup $compact={compact}>
                {buildControls().map((control) => (
                  <ControlIconButton
                    key={control.key}
                    type="button"
                    onClick={control.onClick}
                    disabled={!control.enabled}
                    $compact={compact}
                    $dim={!control.enabled && control.disabledSrc === undefined}
                    title={control.alt}
                  >
                    <img
                      src={!control.enabled && control.disabledSrc ? control.disabledSrc : control.src}
                      alt={control.alt}
                    />
                  </ControlIconButton>
                ))}
              </ControlGroup>
            </BottomWrap>
          </VideoWrap>
          {captureUri !== null && (
            <CaptureWrap>
              <img src={captureUri} alt="screenshot" />
              <CloseButton onClick={() => setCaptureUri(null)}>
                <img src={process.env.PUBLIC_URL + '/images/img_chat_close.svg'} alt="Close" />
              </CloseButton>
            </CaptureWrap>
          )}
        </StreamWrap>
      ) : (
        <EmptyWrap>
          <img src={process.env.PUBLIC_URL + '/images/img_novideo.svg'} alt="No video" />
        </EmptyWrap>
      )}
    </StreamLiveTemplate>
  );
};

const StreamLiveTemplate = styled.div`
  position: relative;
  background: #2d4254;
  border: 1px solid #000000;
  width: ${(props) => (props.tabIndex === 4 ? '23.75rem' : '47.5rem')};
  height: ${(props) => (props.tabIndex === 4 ? '17.8125rem' : '35.625rem')};
  z-index: 1;
`;
const StreamWrap = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
`;
const EmptyWrap = styled.div`
  display: flex;
  width: 100%;
  height: 100%;
  align-items: center;
  justify-content: center;
`;
const HeaderWrap = styled.div`
  height: 2.5rem;
  display: flex;
  ${(props) => props.color && COLOR[props.color]}
`;
const VideoWrap = styled.div`
  height: calc(100% - 5rem);
  display: flex;
  align-items: center;
  justify-content: center;
`;
const BottomWrap = styled.div<{ $compact: boolean }>`
  width: 100%;
  min-height: 30px;
  position: absolute;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  box-sizing: border-box;
  padding: 0 ${(props) => (props.$compact ? '6px' : '10px')};
  gap: ${(props) => (props.$compact ? '4px' : '10px')};
`;
const BottomInfo = styled.div<{ $compact: boolean }>`
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
  overflow: hidden;
  white-space: nowrap;
  font-style: normal;
  font-weight: 500;
  font-size: ${(props) => (props.$compact ? '11px' : '16px')};
  line-height: 1.2;
  color: #ffffff;
`;
const InfoText = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
`;
const ErrorBadge = styled.span`
  flex: none;
  color: #ffd479;
  font-weight: 700;
  cursor: help;
`;
const ControlGroup = styled.div<{ $compact: boolean }>`
  display: flex;
  align-items: center;
  flex: none;
  gap: ${(props) => (props.$compact ? '2px' : '6px')};
`;
const ControlIconButton = styled.button<{ $compact: boolean; $dim: boolean }>`
  appearance: none;
  border: none;
  background: transparent;
  padding: 0;
  line-height: 0;
  flex: none;
  cursor: pointer;
  width: ${(props) => (props.$compact ? '26px' : '32px')};
  height: ${(props) => (props.$compact ? '26px' : '32px')};
  img {
    display: block;
    width: 100%;
    height: 100%;
    object-fit: contain;
  }
  &:disabled {
    cursor: default;
    opacity: ${(props) => (props.$dim ? 0.35 : 1)};
  }
`;
const TextWrap = styled.div`
  margin: auto;
  font-style: normal;
  font-weight: 500;
  font-size: 16px;
  line-height: 19px;
  color: #ffffff;
  text-align: center;
`;
const Image = styled.img`
  display: block;
  position: absolute;
  top: 10px;
  left: 15px;
`;
const RightImage = styled.img`
  display: block;
  position: absolute;
  top: 10px;
  right: 15px;
  cursor: pointer;
`;
const CaptureWrap = styled.div`
  position: absolute;
  top: 2.5rem;
  width: 100%;
  height: calc(100% - 2.5rem);
  text-align: center;
  background: #000;
  z-index: 3;
  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }
`;
const CloseButton = styled.button`
  position: absolute;
  top: 1rem;
  right: 0.5rem;
  background: transparent;
  border: none;
  cursor: pointer;
  z-index: 1;
`;

export default StreamLive;
